"use client";

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MessageSquare, Image as ImageIcon, Music, Play as PlayIcon, Plus, X } from 'lucide-react';
import type { ReactNode } from 'react';

type MiniEdgeMenuProps = {
  onQuickInsert: (type: 'text' | 'image' | 'audio' | 'video') => void;
  onDelete: () => void;
  labels?: {
    insert: string;
    delete: string;
    text: string;
    image: string;
    audio: string;
    video: string;
  };
};

export function MiniEdgeMenu({ onQuickInsert, onDelete, labels }: MiniEdgeMenuProps) {
  const l = {
    insert: labels?.insert ?? 'Insert',
    delete: labels?.delete ?? 'Delete',
    text: labels?.text ?? 'Text',
    image: labels?.image ?? 'Image',
    audio: labels?.audio ?? 'Audio',
    video: labels?.video ?? 'Video',
  };

  return (
    <div className="flex items-center gap-1 rounded-full bg-background/80 backdrop-blur px-1 py-0.5 shadow border">
      <Popover>
        <PopoverTrigger asChild>
          <Button size="icon" variant="secondary" className="h-6 w-6 rounded-full shadow" aria-label={l.insert}>
            <Plus size={12} />
          </Button>
        </PopoverTrigger>
        <PopoverContent sideOffset={6} className="w-auto p-1">
          <div className="grid grid-cols-4 gap-1">
            <IconActionButton label={l.text} onClick={() => onQuickInsert('text')}>
              <MessageSquare size={14} />
            </IconActionButton>
            <IconActionButton label={l.image} onClick={() => onQuickInsert('image')}>
              <ImageIcon size={14} />
            </IconActionButton>
            <IconActionButton label={l.audio} onClick={() => onQuickInsert('audio')}>
              <Music size={14} />
            </IconActionButton>
            <IconActionButton label={l.video} onClick={() => onQuickInsert('video')}>
              <PlayIcon size={14} />
            </IconActionButton>
          </div>
        </PopoverContent>
      </Popover>
      <Button
        size="icon"
        variant="destructive"
        className="h-6 w-6 rounded-full shadow"
        onClick={onDelete}
        aria-label={l.delete}
      >
        <X size={12} />
      </Button>
    </div>
  );
}

function IconActionButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-xs hover:bg-accent"
      title={label}
      aria-label={label}
    >
      {children}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}


